// Sidebar de progression - Stepper vertical pour le parcours diagnostic
// Utilise les composants shadcn/ui Sidebar — tokens only, zéro couleur en dur

import { Link } from 'react-router-dom'
import { LogOut, Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAuth } from '../../contexts/AuthContext'
import { LogoPreventionScop } from './Header'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
} from '../ui/sidebar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'

export type StepStatus = 'completed' | 'current' | 'upcoming'

export interface Step {
  id: string
  title: string
  description: string
  status: StepStatus
  route?: string
  children?: Step[]
  data?: Record<string, unknown>
}

interface ProgressSidebarProps {
  steps: Step[]
  className?: string
  onStepClick?: (step: Step) => void
}

function getInitials(prenom?: string, nom?: string): string {
  const p = prenom?.charAt(0)?.toUpperCase() || ''
  const n = nom?.charAt(0)?.toUpperCase() || ''
  return p + n || '?'
}

export default function ProgressSidebar({
  steps,
  className = '',
  onStepClick,
}: ProgressSidebarProps) {
  const { user, logout } = useAuth()
  const initials = getInitials(user?.prenom, user?.nom)
  const fullName = user ? `${user.prenom} ${user.nom}` : 'Utilisateur'
  const email = user?.email || ''

  const isStepClickable = (step: Step) => {
    return (step.status === 'completed' || step.status === 'current') && step.route && onStepClick
  }

  return (
    <Sidebar collapsible="icon" className={className}>
      {/* Header : logo + trigger */}
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-2">
          <span className="group-data-[collapsible=icon]:hidden">
            <Link to="/dashboard">
              <LogoPreventionScop className="h-8 w-auto" />
            </Link>
          </span>
          <SidebarTrigger className="ml-auto" />
        </div>
      </SidebarHeader>

      {/* Contenu : stepper */}
      <SidebarContent>
        {/* Stepper vertical — masqué en mode icône */}
        <nav className="flex-1 px-4 pt-4 pb-4 group-data-[collapsible=icon]:hidden">
          <ol className="space-y-0">
            {steps.map((step, index) => {
              const isLast = index === steps.length - 1
              const isCurrent = step.status === 'current'

              // Children visibles si parent current ou un enfant current
              const hasActiveChildren = step.children && step.children.length > 0 && (
                step.status === 'current' || step.children.some(c => c.status === 'current')
              )

              // Vérifier si l'étape a des enfants et si tous sont complétés
              const hasChildren = step.children && step.children.length > 0
              const allChildrenCompleted = step.children?.every(
                child => child.status === 'completed'
              ) ?? false

              // L'étape suivante complétée implique que celle-ci l'est aussi
              const nextStep = steps[index + 1]
              const nextStepCompleted = nextStep && nextStep.status === 'completed'

              // Visuellement complétée si :
              // - status completed
              // - OU tous les enfants sont complétés
              // - OU l'étape suivante est complétée
              const visuallyCompleted = step.status === 'completed' ||
                (hasChildren && allChildrenCompleted) ||
                nextStepCompleted

              return (
                <li key={step.id} className="flex gap-4">
                  {/* ========== COLONNE GAUCHE : Cercle + Ligne ========== */}
                  <div className="flex flex-col items-center">
                    {/* Cercle */}
                    <div
                      className={cn(
                        "flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 transition-colors",
                        visuallyCompleted
                          ? "bg-primary text-primary-foreground"
                          : isCurrent
                            ? "bg-primary text-primary-foreground ring-4 ring-primary/20"
                            : "bg-muted text-muted-foreground",
                        isStepClickable(step) && "cursor-pointer"
                      )}
                      onClick={() => isStepClickable(step) && onStepClick?.(step)}
                      role={isStepClickable(step) ? 'button' : undefined}
                      tabIndex={isStepClickable(step) ? 0 : undefined}
                      onKeyDown={(e) => {
                        if (isStepClickable(step) && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault()
                          onStepClick?.(step)
                        }
                      }}
                    >
                      {visuallyCompleted && !isCurrent ? (
                        <Check className="size-4" />
                      ) : (
                        <span className="w-2.5 h-2.5 rounded-full bg-current" />
                      )}
                    </div>

                    {/* Ligne verticale */}
                    {!isLast && (
                      <div className={cn(
                        "w-0.5 flex-1 min-h-4",
                        visuallyCompleted ? "bg-primary" :
                        isCurrent ? "bg-primary/40" :
                        "bg-border"
                      )} />
                    )}
                  </div>

                  {/* ========== COLONNE DROITE : Contenu ========== */}
                  <div className="flex-1 pb-6">
                    {/* Titre + description */}
                    <div
                      className={cn(
                        isStepClickable(step) && "-mx-2 px-2 py-1 rounded-lg cursor-pointer hover:bg-muted transition-colors"
                      )}
                      onClick={() => isStepClickable(step) && onStepClick?.(step)}
                    >
                      <p
                        className={cn(
                          "text-sm font-semibold",
                          visuallyCompleted ? "text-primary" :
                          isCurrent ? "text-foreground" :
                          "text-muted-foreground",
                          isStepClickable(step) && "cursor-pointer hover:underline"
                        )}
                      >
                        {step.title}
                      </p>
                      {step.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                      )}
                    </div>

                    {/* ========== CHILDREN ========== */}
                    {hasActiveChildren && step.children && (
                      <ul className="mt-3 border-l-2 border-border pl-3 space-y-2">
                        {step.children.map((child) => {
                          const isChildClickable = (child.status === 'completed' || child.status === 'current') && child.route && onStepClick
                          const isChildCurrent = child.status === 'current'
                          const childCompleted = child.status === 'completed'

                          return (
                            <li
                              key={child.id}
                              className={cn(
                                "flex items-center gap-2 text-xs font-medium",
                                childCompleted ? "text-primary" :
                                isChildCurrent ? "text-foreground" :
                                "text-muted-foreground",
                                isChildClickable && "cursor-pointer hover:underline"
                              )}
                              onClick={() => isChildClickable && onStepClick?.(child)}
                              role={isChildClickable ? 'button' : undefined}
                              tabIndex={isChildClickable ? 0 : undefined}
                              onKeyDown={(e) => {
                                if (isChildClickable && (e.key === 'Enter' || e.key === ' ')) {
                                  e.preventDefault()
                                  onStepClick?.(child)
                                }
                              }}
                            >
                              {childCompleted ? (
                                <Check className="size-3 flex-shrink-0" />
                              ) : isChildCurrent ? (
                                <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                              ) : (
                                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                              )}
                              {child.title}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        </nav>
      </SidebarContent>

      {/* Footer : utilisateur connecté */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg">
                  <div className="flex aspect-square size-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <span className="text-xs font-medium">{initials}</span>
                  </div>
                  <div className="flex flex-col gap-0.5 leading-none">
                    <span className="font-semibold text-sm">{fullName}</span>
                    <span className="text-xs text-muted-foreground">{email}</span>
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="top" className="w-[--radix-dropdown-menu-trigger-width]">
                <DropdownMenuItem onClick={() => logout()}>
                  <LogOut className="size-4 mr-2" />
                  Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}

// Version mobile - Barre horizontale compacte
export function ProgressBarMobile({ steps }: { steps: Step[] }) {
  const totalSteps = steps.length
  const completedSteps = steps.filter(s => s.status === 'completed').length
  const currentStep = steps.findIndex(s => s.status === 'current')
  const progress = ((completedSteps + (currentStep >= 0 ? 0.5 : 0)) / totalSteps) * 100

  return (
    <div className="md:hidden bg-card border-b border-border px-4 py-3">
      {/* Barre de progression */}
      <div className="flex items-center gap-3 mb-2">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-xs font-medium text-muted-foreground">
          {completedSteps + 1}/{totalSteps}
        </span>
      </div>

      {/* Étape actuelle */}
      {currentStep >= 0 && (
        <p className="text-sm font-medium text-foreground">
          {steps[currentStep].title}
        </p>
      )}
    </div>
  )
}
